import React from "react";
import { useRootStore } from "../../../../store/contexts";
import { ShowMultiLine } from "../../../common/controls";
import { ShowReference } from "../../../common/controls";
import { countryDisplayId } from "../../../country/store/CountryCommonStore";
import { GroupContainer } from "../../../../common/components";
import { provinceStateDisplayId } from "../../../provinceState/store/ProvinceStateCommonStore";
import { ShowItemsCompact } from "../../../common/controls";
import { useSnapshotModalStack } from "../../../common/controls";
import { EditionView as SnapshotEditionView } from "./EditionView";
import { EditionTypeView as SnapshotEditionTypeView } from "./EditionTypeView";
import { MusicalWorkView as SnapshotMusicalWorkView } from "./MusicalWorkView";
import { PhonogramView as SnapshotPhonogramView } from "./PhonogramView";
import { MusicalWorkLinkView as SnapshotMusicalWorkLinkView } from "./MusicalWorkLinkView";
import { PlatformView as SnapshotPlatformView } from "./PlatformView";
import { PlatformLinkTypeView as SnapshotPlatformLinkTypeView } from "./PlatformLinkTypeView";
import { MusicalGenreView as SnapshotMusicalGenreView } from "./MusicalGenreView";
import { InterpreterView as SnapshotInterpreterView } from "./InterpreterView";
import { TerritoryView as SnapshotTerritoryView } from "./TerritoryView";
import { WorkAuthorView as SnapshotWorkAuthorView } from "./WorkAuthorView";
import { AuthorComposerView as SnapshotAuthorComposerView } from "./AuthorComposerView";
import { CountryView as SnapshotCountryView } from "./CountryView";
import { ProvinceStateView as SnapshotProvinceStateView } from "./ProvinceStateView";
import { AuthorCollectingSocietyView as SnapshotAuthorCollectingSocietyView } from "./AuthorCollectingSocietyView";
import { CollectiveManagementSocietyView as SnapshotCollectiveManagementSocietyView } from "./CollectiveManagementSocietyView";
import { WorkAuthorPublisherView as SnapshotWorkAuthorPublisherView } from "./WorkAuthorPublisherView";
import { PublisherRepresentativeView as SnapshotPublisherRepresentativeView } from "./PublisherRepresentativeView";
import { PublisherCollectingSocietyView as SnapshotPublisherCollectingSocietyView } from "./PublisherCollectingSocietyView";
import { EditionDocumentationView as SnapshotEditionDocumentationView } from "./EditionDocumentationView";
import { EditionContractPublisherView as SnapshotEditionContractPublisherView } from "./EditionContractPublisherView";
import { AssociatedDocumentEditionView as SnapshotAssociatedDocumentEditionView } from "./AssociatedDocumentEditionView";
import { EditionDocumentTypeView as SnapshotEditionDocumentTypeView } from "./EditionDocumentTypeView";
import { UserAttachmentEditionView as SnapshotUserAttachmentEditionView } from "./UserAttachmentEditionView";
import {
  useSnapshotExtension,
  useSnapshotExtensionTop,
} from "../../../common/controls";
import { FormControl } from "@mui/material";

type PublisherViewProps = {
  snapshotData: any;
  // Propagated from the top-level snapshot view so nested tables can resolve
  // the snapshot-scoped file-download endpoint (/<snapshotEntityName>.data/
  // <snapshotRecordId>/<fileUuid>). Both are optional at the type level but
  // required at runtime for the download / view affordances to work.
  snapshotEntityName?: string;
  snapshotRecordId?: string;
};

export const PublisherView: React.FC<PublisherViewProps> = ({
  snapshotData,
  snapshotEntityName,
  snapshotRecordId,
}) => {
  const { langStore } = useRootStore();
  const renderExtensionTop = useSnapshotExtensionTop("Publisher");
  const renderExtension = useSnapshotExtension("Publisher");

  const { pushModal, modalElement } = useSnapshotModalStack(
    (entityName, data) => {
      switch (entityName) {
        case "Edition":
          return (
            <SnapshotEditionView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "EditionType":
          return (
            <SnapshotEditionTypeView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "MusicalWork":
          return (
            <SnapshotMusicalWorkView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Phonogram":
          return (
            <SnapshotPhonogramView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "MusicalWorkLink":
          return (
            <SnapshotMusicalWorkLinkView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Platform":
          return (
            <SnapshotPlatformView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "PlatformLinkType":
          return (
            <SnapshotPlatformLinkTypeView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "MusicalGenre":
          return (
            <SnapshotMusicalGenreView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Interpreter":
          return (
            <SnapshotInterpreterView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Territory":
          return (
            <SnapshotTerritoryView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "WorkAuthor":
          return (
            <SnapshotWorkAuthorView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "AuthorComposer":
          return (
            <SnapshotAuthorComposerView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Country":
          return (
            <SnapshotCountryView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "ProvinceState":
          return (
            <SnapshotProvinceStateView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "AuthorCollectingSociety":
          return (
            <SnapshotAuthorCollectingSocietyView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "CollectiveManagementSociety":
          return (
            <SnapshotCollectiveManagementSocietyView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "WorkAuthorPublisher":
          return (
            <SnapshotWorkAuthorPublisherView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "PublisherRepresentative":
          return (
            <SnapshotPublisherRepresentativeView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "PublisherCollectingSociety":
          return (
            <SnapshotPublisherCollectingSocietyView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "EditionDocumentation":
          return (
            <SnapshotEditionDocumentationView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "EditionContractPublisher":
          return (
            <SnapshotEditionContractPublisherView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "AssociatedDocumentEdition":
          return (
            <SnapshotAssociatedDocumentEditionView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "EditionDocumentType":
          return (
            <SnapshotEditionDocumentTypeView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "UserAttachmentEdition":
          return (
            <SnapshotUserAttachmentEditionView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        default:
          return null;
      }
    }
  );
  return (
    <>
      {renderExtensionTop ? renderExtensionTop(snapshotData) : null}
      <GroupContainer
        id="basicInformation"
        title={langStore.groupPublisherBasicInformationLabel}
      >
        <FormControl fullWidth>
          <ShowMultiLine
            label={langStore.PublisherPersonTypeLabel}
            value={String(snapshotData?.personType ?? "")}
          />
          <ShowMultiLine
            label={langStore.PublisherNameLabel}
            value={String(snapshotData?.name ?? "")}
          />
          <ShowMultiLine
            label={langStore.PublisherMaritalStatusLabel}
            value={String(snapshotData?.maritalStatus ?? "")}
          />
          <ShowReference
            label={langStore.PublisherNationalityLabel}
            selectedValue={
              snapshotData?.nationality
                ? langStore.displayIdByLang(
                    countryDisplayId(snapshotData?.nationality, langStore)
                  )
                : ""
            }
            onView={() => pushModal("Country", snapshotData?.nationality)}
          />
          <ShowMultiLine
            label={langStore.PublisherPseudonymLabel}
            value={String(snapshotData?.pseudonym ?? "")}
          />
          <ShowMultiLine
            label={langStore.PublisherTaxIdLabel}
            value={String(snapshotData?.taxId ?? "")}
          />
          <ShowMultiLine
            label={langStore.PublisherPhoneLabel}
            value={String(snapshotData?.phone ?? "")}
          />
          <ShowMultiLine
            label={langStore.PublisherEmailLabel}
            value={String(snapshotData?.email ?? "")}
          />
        </FormControl>
      </GroupContainer>
      <GroupContainer id="address" title={langStore.groupPublisherAddressLabel}>
        <FormControl fullWidth>
          <ShowMultiLine
            label={langStore.PublisherStreetAndNumberLabel}
            value={String(snapshotData?.streetAndNumber ?? "")}
          />
          <ShowMultiLine
            label={langStore.PublisherFloorLabel}
            value={String(snapshotData?.floor ?? "")}
          />
          <ShowMultiLine
            label={langStore.PublisherSuiteLabel}
            value={String(snapshotData?.suite ?? "")}
          />
          <ShowMultiLine
            label={langStore.PublisherPostalCodeLabel}
            value={String(snapshotData?.postalCode ?? "")}
          />
          <ShowReference
            label={langStore.PublisherCountryLabel}
            selectedValue={
              snapshotData?.country
                ? langStore.displayIdByLang(
                    countryDisplayId(snapshotData?.country, langStore)
                  )
                : ""
            }
            onView={() => pushModal("Country", snapshotData?.country)}
          />
          <ShowReference
            label={langStore.PublisherProvinceLabel}
            selectedValue={
              snapshotData?.province
                ? langStore.displayIdByLang(
                    provinceStateDisplayId(snapshotData?.province, langStore)
                  )
                : ""
            }
            onView={() => pushModal("ProvinceState", snapshotData?.province)}
          />
          <ShowMultiLine
            label={langStore.PublisherCityLabel}
            value={String(snapshotData?.city ?? "")}
          />
        </FormControl>
      </GroupContainer>
      <GroupContainer
        id="legalInformation"
        title={langStore.groupPublisherLegalInformationLabel}
      >
        <FormControl fullWidth>
          <ShowMultiLine
            label={langStore.PublisherCaeIpiLabel}
            value={String(snapshotData?.caeIpi ?? "")}
          />
          <ShowItemsCompact
            label={langStore.PublisherRepresentativesLabel}
            showLabel={true}
            enableDownload={true}
            entityName="PublisherRepresentative"
            snapshotEntityName={snapshotEntityName}
            snapshotRecordId={snapshotRecordId}
            columns={[
              {
                name: "name",
                title: langStore.PublisherRepresentativeNameLabel,
              },
              {
                name: "role",
                title: langStore.PublisherRepresentativeRoleLabel,
              },
              {
                name: "email",
                title: langStore.PublisherRepresentativeEmailLabel,
              },
              {
                name: "phone",
                title: langStore.PublisherRepresentativePhoneLabel,
              },
              {
                name: "isSigner",
                title: langStore.PublisherRepresentativeIsSignerLabel,
              },
            ]}
            rows={(snapshotData?.representatives ?? []).map(
              (__r: any, __i: number) => ({
                __status__: "original",
                id: String(__i),
                name: __r?.name ?? "",
                role: __r?.role ?? "",
                email: __r?.email ?? "",
                phone: __r?.phone ?? "",
                isSigner: __r?.isSigner ?? "",
              })
            )}
            onRowClick={(rowId: string) => {
              const idx = parseInt(rowId, 10);
              pushModal(
                "PublisherRepresentative",
                (snapshotData?.representatives ?? [])[idx]
              );
            }}
          />
          <ShowItemsCompact
            label={langStore.PublisherManagementSocietiesLabel}
            showLabel={true}
            enableDownload={true}
            entityName="PublisherCollectingSociety"
            snapshotEntityName={snapshotEntityName}
            snapshotRecordId={snapshotRecordId}
            columns={[
              {
                name: "cmsAccountNumber",
                title:
                  langStore.PublisherCollectingSocietyCmsAccountNumberLabel,
              },
            ]}
            rows={(snapshotData?.managementSocieties ?? []).map(
              (__r: any, __i: number) => ({
                __status__: "original",
                id: String(__i),
                cmsAccountNumber: __r?.cmsAccountNumber ?? "",
              })
            )}
            onRowClick={(rowId: string) => {
              const idx = parseInt(rowId, 10);
              pushModal(
                "PublisherCollectingSociety",
                (snapshotData?.managementSocieties ?? [])[idx]
              );
            }}
          />
        </FormControl>
      </GroupContainer>
      {renderExtension ? renderExtension(snapshotData) : null}

      {modalElement}
    </>
  );
};
